import { createHttpService } from 'src/apis/ApiService';
import { TPaginationParams } from 'src/apis/__shared/shared.interfaces';
import { mockGuardianFees } from 'src/__mocks__/guardian/guardianFees.mock';
import { TFeeComponent } from 'src/apis/admissions/adminssionFees.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianFee = {
  id: string;
  feeType: string;
  amount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
  paymentDate?: string;
  transactionId?: string;
  feeComponents?: TFeeComponent[];
};

const useGuardianFeesService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/fees';
  const withMockData = isAppWithMock();

  const getFees = (
    studentId: string,
    params?: TPaginationParams & { status?: string; academicYear?: string }
  ): Promise<{ data: GuardianFee[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianFees];
      
      if (params?.status) {
        filteredData = filteredData.filter((f) => f.status === params.status);
      }
      if (params?.academicYear) {
        filteredData = filteredData.filter((f) => {
          const feeYear = new Date(f.dueDate).getFullYear().toString();
          return feeYear === params.academicYear;
        });
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.status && { status: params.status }),
        ...(params?.academicYear && { academicYear: params.academicYear }),
      },
    };

    return httpAPI
      .get<{ data: GuardianFee[]; count: number }>(baseGuardianPath, requestObj)
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: any[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian fees:', error);
        return { data: [], count: 0 };
      });
  };

  const payFee = (
    studentId: string,
    feeId: string,
    paymentData: { amount: number; paymentMethod: string; transactionId?: string; transactionDateTime?: string }
  ): Promise<{ success: boolean; message: string }> => {
    if (withMockData) {
      return Promise.resolve({
        success: true,
        message: 'Payment processed successfully',
      });
    }

    return httpAPI
      .post<{ success: boolean; message: string }>(
        `${baseGuardianPath}/${feeId}/pay`,
        { studentId, ...paymentData }
      )
      .then((response) => response as { success: boolean; message: string })
      .catch((error) => {
        console.error('Error processing fee payment:', error);
        throw error;
      });
  };

  return {
    getFees,
    payFee,
  };
};

export default useGuardianFeesService;
